import {useQuery} from '@apollo/client';
import {Select} from 'antd';
import {GET_ALL_INDUSTRIES} from 'graphql/queries/report';
import {GET_ALL_COUNTRIES} from 'graphql/queries/startupCompanies';
import {GET_ALL_STOCKS_WITH_INSTRUMENT_KEY} from 'graphql/queries/stocks';
import {GET_ALL_RISK_PROFILES} from 'graphql/queries/users';
import {GET_ALL_COLLECTION_GROUPS} from 'graphql/queries/collections';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {
  ID_FIELD_SOURCES,
  humanizeField,
  isGqlError,
  looksLikeObjectId,
} from './constants';

/**
 * Entity picker for ID-typed filter fields.
 *
 * Known field names (industries, countries, exchange…) resolve to a labelled
 * dropdown from the matching entity query. Any unrecognized ID field falls back
 * to a free tag input where the admin pastes ObjectId(s) — so the form stays
 * fully metadata-driven and new ID fields keep working without code changes.
 */
const useEntityOptions = (source, branch) => {
  // `id` and `group` list within the branch's own asset class and instrument
  // type, so they cannot load until those two are chosen.
  const scoped = Boolean(branch?.assetClassKey && branch?.instrumentKey);
  const instruments = useQuery(GET_ASSET_CLASS_INSTRUMENTS, {
    variables: {
      input: {
        assetClassKey: branch?.assetClassKey,
        instrumentKey: branch?.instrumentKey,
        explore: false,
        paginate: true,
        page: 1,
        limit: 50,
        ...(source === 'INSTRUMENT_GROUP' && {groups: true}),
      },
    },
    skip: !scoped || (source !== 'INSTRUMENT' && source !== 'INSTRUMENT_GROUP'),
    errorPolicy: 'all',
  });

  const industries = useQuery(GET_ALL_INDUSTRIES, {
    skip: source !== 'INDUSTRY',
    errorPolicy: 'all',
  });
  const countries = useQuery(GET_ALL_COUNTRIES, {
    skip: source !== 'COUNTRY',
    errorPolicy: 'all',
  });
  const exchanges = useQuery(GET_ALL_STOCKS_WITH_INSTRUMENT_KEY, {
    skip: source !== 'EXCHANGE',
    errorPolicy: 'all',
  });
  const riskProfiles = useQuery(GET_ALL_RISK_PROFILES, {
    skip: source !== 'RISK_PROFILE',
    errorPolicy: 'all',
  });
  const collectionGroups = useQuery(GET_ALL_COLLECTION_GROUPS, {
    variables: {filters: {page: 1, limit: 100, paginate: true}},
    skip: source !== 'COLLECTION_GROUP',
    errorPolicy: 'all',
  });

  if (source === 'INDUSTRY') {
    const list = industries.data?.getAllIndustries;
    return {
      loading: industries.loading,
      options: (Array.isArray(list) ? list : [])
        .filter(item => !isGqlError(item))
        .map(item => ({value: item.id, label: item.name})),
    };
  }
  if (source === 'COUNTRY') {
    const list = countries.data?.getAllCountries;
    return {
      loading: countries.loading,
      options: (Array.isArray(list) ? list : [])
        .filter(item => !isGqlError(item))
        .map(item => ({value: item.id, label: item.name})),
    };
  }
  if (source === 'EXCHANGE') {
    const list = exchanges.data?.getAllStocksExchange;
    return {
      loading: exchanges.loading,
      options: (Array.isArray(list) ? list : [])
        .filter(item => !isGqlError(item))
        .map(item => ({
          value: item.id,
          label: item.stockExchangeName || item.code,
        })),
    };
  }
  if (source === 'INSTRUMENT' || source === 'INSTRUMENT_GROUP') {
    if (!scoped) return {loading: false, options: [], blocked: true};
    const rows = instruments.data?.getFinancialInstruments?.data || [];
    return {
      loading: instruments.loading,
      options: rows
        .map(item =>
          source === 'INSTRUMENT_GROUP'
            ? {value: item.groupId, label: item.title}
            : {value: item.id, label: item.name},
        )
        .filter(option => option.value && option.label),
    };
  }
  if (source === 'RISK_PROFILE') {
    const result = riskProfiles.data?.getAllRiskProfiles;
    return {
      loading: riskProfiles.loading,
      options: isGqlError(result)
        ? []
        : (result?.data || []).map(item => ({
            value: item.id,
            label: item.name,
          })),
    };
  }
  if (source === 'COLLECTION_GROUP') {
    const result = collectionGroups.data?.getAllCollectionGroups;
    return {
      loading: collectionGroups.loading,
      options: isGqlError(result)
        ? []
        : (result?.data?.collectionGroups || []).map(item => ({
            value: item.id,
            label: item.title,
          })),
    };
  }
  return {loading: false, options: null};
};

export const IdField = ({field, branch, value, onChange, disabled}) => {
  const source = ID_FIELD_SOURCES[field.name];
  const {loading, options, blocked} = useEntityOptions(source, branch);
  const multiple = field.isList;

  // Unknown ID field → paste ObjectId(s) as tags, validating the shape.
  if (!options) {
    return (
      <Select
        mode="tags"
        allowClear
        disabled={disabled}
        value={value}
        placeholder="Paste ObjectId(s)"
        onChange={next => {
          const list = (Array.isArray(next) ? next : [next]).filter(
            looksLikeObjectId,
          );
          onChange(multiple ? list : list[list.length - 1]);
        }}
        tokenSeparators={[',', ' ']}
      />
    );
  }

  return (
    <Select
      showSearch
      allowClear
      optionFilterProp="label"
      mode={multiple ? 'multiple' : undefined}
      loading={loading}
      disabled={disabled || blocked}
      value={value}
      options={options}
      // "No data" on an empty picker reads as a failure; say why it is empty.
      notFoundContent={
        loading ? null : `Nothing here for this asset class and instrument type`
      }
      placeholder={
        blocked
          ? 'Pick an asset class and type first'
          : `Select ${humanizeField(field.name).toLowerCase()}`
      }
      onChange={onChange}
    />
  );
};

export default IdField;
